import { useState, useEffect, useContext, useMemo } from "react";
import { FaEye, FaTrash, FaSearch, FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { DataProvider } from "../../../store/ContextProvider";

export default function AdminUsers() {
  const { BeURL, showAlert } = useContext(DataProvider);

  // list state
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10, totalPages: 1, total: 0 });

  const navigate = useNavigate()

  // filters (source of truth for fetch)
  const [filters, setFilters] = useState({
    page: 1,
    perPage: 10,
    search: "",
    role: "",
    sortField: "firstName",
    sortOrder: "asc",
  });

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  
  
  // debounced search application
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({
        ...f,
        page: 1,
        search: searchTerm.trim(),
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // fetch users with abort safety
  useEffect(() => {
    const ctrl = new AbortController();
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const qs = new URLSearchParams(filters).toString();
        const res = await fetch(`${BeURL}/admin/users?${qs}`, {
          credentials: "include",
          signal: ctrl.signal,
        });
        const data = await res.json();
        if (data?.success) {
          setUsers(Array.isArray(data.users) ? data.users : []);
          setPagination(
            data.pagination ?? {
              page: filters.page,
              perPage: filters.perPage,
              totalPages: 1,
              total: Array.isArray(data.users) ? data.users.length : 0,
            }
          );
        } else {
          showAlert(data?.message || "Unable to load users.");
        }
      } catch (e) {
        if (e.name !== "AbortError") showAlert("Unable to load users.");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BeURL, filters]);

  // sort / filter helpers
  const sortBy = (field) =>
    setFilters((f) => ({
      ...f,
      sortField: field,
      sortOrder: f.sortField === field && f.sortOrder === "asc" ? "desc" : "asc",
      page: 1,
    }));
  const changeRole = (role) => setFilters((f) => ({ ...f, role, page: 1 }));
  const changePerPage = (n) => setFilters((f) => ({ ...f, perPage: Number(n), page: 1 }));
  const goPage = (delta) =>
    setFilters((f) => ({
      ...f,
      page: Math.max(1, Math.min(f.page + delta, pagination.totalPages || 1)),
    }));

  // search button enablement
  const canSearch = useMemo(() => {
    const s = searchTerm.trim();
    return s.length === 0 || s.length >= 3;
  }, [searchTerm]);

  // view user
  const openView = async (id) => {

    window.open(`/user/${id}`, "_blank")

    // try {
    //   const r = await fetch(`${BeURL}/admin/users/${id}`, { credentials: "include" });
    //   const d = await r.json();
    //   d?.success ? setViewUser(d.user) : showAlert(d?.message || "Cannot fetch user.");
    // } catch {
    //   showAlert("Cannot fetch user.");
    // }
  };



  // delete flow
  const confirmDelete = (id) => setToDelete(id);
  const doDelete = async () => {
    if (!toDelete) return;
    try {
      const r = await fetch(`${BeURL}/admin/users/${toDelete}`, {
        method: "DELETE",
        credentials: "include",
      });
      const d = await r.json();
      showAlert(d?.message || "Delete attempt finished.");
      if (d?.success) {
        // After delete, refetch current page (filters effect will refetch automatically if we nudge the state)
        setFilters((f) => ({ ...f })); // trigger refetch
      }
    } catch {
      showAlert("Delete failed.");
    } finally {
      setToDelete(null);
    }
  };

  const columns = [
    { key: "fullName", label: "Full Name", sortKey: "fullName" },
    { key: "email", label: "Email", sortKey: "email" },
    { key: "contact", label: "Contact", sortKey: "contact" },
    { key: "role", label: "Role", sortKey: "role" },
  ];



  return (
    <div className="p-6 space-y-4 min-h-[80vh] relative">
      <div className='flex flex-wrap gap-6 justify-between my-3'>
        <h1 className="text-2xl font-semibold text-primary-400">User Management</h1>
        <button className='rounded-full px-4 py-1 text-md bg-secondary-500 hover:bg-secondary-600 text-white' onClick={() => window.location.href= '/signUpEmployer'}>Create Employer</button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-1 max-w-md">
          <input
            type="search"
            placeholder="Search users (min 3 chars)…"
            className="flex-1 border rounded-l px-3 py-2 focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            disabled={!canSearch}
            onClick={() =>
              setFilters((f) => ({ ...f, search: searchTerm.trim(), page: 1 }))
            }
            className="px-4 bg-primary-300 hover:bg-primary-400 disabled:opacity-60 text-white rounded-r flex items-center"
            title="Apply search"
          >
            <FaSearch className="mr-2" /> Search
          </button>
        </div>

        <select
          className="border rounded px-3 py-2"
          value={filters.role}
          onChange={(e) => changeRole(e.target.value)}
        >
          <option value="">All Roles</option>
          {["admin", "employer", "job-seeker"].map((r) => (
            <option key={r} value={r}>
              {r.replace("-", " ").toUpperCase()}
            </option>
          ))}
        </select>

        <select
          className="border rounded px-3 py-2"
          value={filters.perPage}
          onChange={(e) => changePerPage(e.target.value)}
        >
          {[5, 10, 20, 50].map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="w-full table-auto">
          <thead className="text-primary-400 bg-blue-50">
            <tr>
              {columns.map(({ key, label, sortKey }) => (
                <th
                  key={key}
                  className="p-3 text-left cursor-pointer select-none font-semibold"
                  onClick={() => sortBy(sortKey)}
                >
                  {label}
                  {filters.sortField === sortKey && (filters.sortOrder === "asc" ? " ▲" : " ▼")}
                </th>
              ))}
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="p-6 text-center">
                  Loading…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="p-6 text-center">
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((u, i) => (
                <tr
                  key={u._id}
                  className={`border-b hover:bg-gray-50 ${i % 2 === 0 ? "" : "bg-slate-50"}`}
                >
                  <td className="p-3">{u.fullName}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">{u.contact}</td>
                  <td className="p-3">{u.role}</td>
                  <td className="p-3 text-center space-x-2">
                    <button
                      onClick={() => openView(u._id)}
                      className="text-slate-600 hover:text-slate-800"
                      title="View"
                    >
                      <FaEye />
                    </button>

                  
                      <button
                        onClick={() => navigate(`/edit-user/${u._id}`) }
                        className="text-slate-600 hover:text-slate-800"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                 

                    <button
                      onClick={() => confirmDelete(u._id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-4">
        <button
          disabled={filters.page === 1}
          onClick={() => goPage(-1)}
          className="px-3 py-1 bg-white border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <button
          disabled={pagination.page === pagination.totalPages}
          onClick={() => goPage(+1)}
          className="px-3 py-1 bg-white border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* View Modal */}
      {viewUser && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full space-y-2">
            <h3 className="text-xl font-semibold text-primary-400">User Details</h3>
            {["_id", "firstName", "lastName", "email", "contact", "role"].map((k) => (
              <div key={k}>
                <span className="font-medium">
                  {k === "_id" ? "ID" : k.replace(/([A-Z])/g, " $1")}:
                </span>{" "}
                <span>{k === "_id" ? viewUser?._id : viewUser?.[k]}</span>
              </div>
            ))}
            <button
              onClick={() => setViewUser(null)}
              className="mt-4 px-4 py-2 bg-primary-400 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

    

      {/* Confirm Delete Modal */}
      {toDelete && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full space-y-3">
            <h3 className="text-lg font-semibold">Confirm delete</h3>
            <p>Are you sure you want to delete this user?</p>
            <div className="flex gap-2 justify-end">
              <button
                className="px-3 py-1 rounded bg-slate-200"
                onClick={() => setToDelete(null)}
              >
                Cancel
              </button>
              <button className="px-3 py-1 rounded bg-red-600 text-white" onClick={doDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
